import React, { memo, useState } from 'react'
import CertificationModal from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/CertificationModal'
import { getCertificationDefaultValues } from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/utils/getCertificationDefaultValues'
import { Button } from 'transfix-ui/components/Button'
import Card from 'transfix-ui/components/Card'
import { makeStyles } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { brandColors } from 'transfix-ui/theme/colors'
import { ICertificationCode } from 'types/FMSGraphqlTypes'
import { useModalState } from 'utils/hooks'
import CarrierDiverseCertificationsCardRow from './components/CarrierDiverseCertificationsCardRow'
import { ICarrierProfileCertificationFragment } from './graphql/CarrierProfileCertificationFragment'
import { useCarrierDiverseCertificationsCardSubmitter } from './hooks'

export interface ICarrierDiverseCertificationsCardProps {
  carrierApplicationId: string
  certifications: ICarrierProfileCertificationFragment[]
}

const useCarrierDiverseCertificationsCardStyles = makeStyles(theme => ({
  iconWrapper: {
    color: brandColors.skyBlue6,
    marginLeft: theme.spacing(1),
  },
  certificationRow: {
    borderTop: `1px solid ${brandColors.coolGray2}`,
    padding: `${theme.spacing(2)} 0`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  certificationEdit: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  title: {
    paddingBottom: theme.spacing(2.5),
  },
  certificationContent: {
    marginRight: theme.spacing(2),
  },
}))
const CarrierDiverseCertificationsCard = memo<ICarrierDiverseCertificationsCardProps>(
  ({ carrierApplicationId, certifications }) => {
    const classes = useCarrierDiverseCertificationsCardStyles()
    const existingCertificationCodes = certifications.map(({ code }) => code as ICertificationCode)
    const [currentCertification, setCurrentCertification] = useState<
      ICarrierProfileCertificationFragment
    >()
    const { createToggleHandler, modalState } = useModalState({
      certificationModal: false,
    })
    const { certificationModal: isCertificationsModalOpen } = modalState || {}
    const toggleCertificationModalHandler = createToggleHandler('certificationModal')

    const { deleteHandler, loading, submitHandler } = useCarrierDiverseCertificationsCardSubmitter(
      carrierApplicationId,
      currentCertification?.code as ICertificationCode | undefined
    )

    const editCertificationHandler = (certification: ICarrierProfileCertificationFragment) => {
      setCurrentCertification(certification)
      toggleCertificationModalHandler()
    }

    const closeCertificationModalHandler = () => {
      setCurrentCertification(undefined)
      toggleCertificationModalHandler()
    }

    const defaultValues = getCertificationDefaultValues(currentCertification)

    return (
      <Card>
        <Typography variant='h3' className={classes.title}>
          Diverse Carrier Certifications
        </Typography>
        {certifications.map(certification => (
          <CarrierDiverseCertificationsCardRow
            key={`carrier-certification-${certification.certificationId}`}
            certification={certification}
            onClick={() => editCertificationHandler(certification)}
          />
        ))}
        <div>
          <Button variant='text' onClick={toggleCertificationModalHandler}>
            Add Certifications
          </Button>
        </div>
        {isCertificationsModalOpen && (
          <CertificationModal
            defaultValues={defaultValues}
            loading={loading}
            onSubmit={submitHandler}
            onDelete={deleteHandler}
            onClose={closeCertificationModalHandler}
            existingCertificationCodes={existingCertificationCodes}
          />
        )}
      </Card>
    )
  }
)

export default CarrierDiverseCertificationsCard
